import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { FileText, Users, DollarSign, ArrowLeft, TrendingUp, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      const [statsRes, usersRes, paymentsRes, resumesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/payments`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/resumes`, { headers, withCredentials: true })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPayments(paymentsRes.data);
      setResumes(resumesRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure? This will delete the user and all their data.")) return;
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.delete(`${API}/admin/users/${userId}`, { headers, withCredentials: true });
      toast.success("User deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete user");
    }
  };

  const makeAdmin = async (userId, isAdmin) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.put(`${API}/admin/users/${userId}/role`, { is_admin: isAdmin }, { headers, withCredentials: true });
      toast.success("User role updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update role");
    }
  };

  const toggleVerification = async (userId, currentStatus) => {
    const action = currentStatus ? "unverify" : "verify";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.put(
        `${API}/admin/users/${userId}/verify`,
        { is_verified: !currentStatus },
        { headers, withCredentials: true }
      );
      toast.success(`User ${currentStatus ? 'unverified' : 'verified'} manually`);
      fetchData();
    } catch (e) {
      toast.error(`Failed to ${action} user`);
    }
  };

  const deleteResume = async (resumeId) => {
    if (!window.confirm("Are you sure? This action cannot be undone.")) return;
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.delete(`${API}/admin/resumes/${resumeId}`, { headers, withCredentials: true });
      toast.success("Resume deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete resume");
    }
  };

  const markAsPaid = async (resumeId) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.post(`${API}/admin/payments/manual`, { resume_id: resumeId }, { headers, withCredentials: true });
      toast.success("Resume marked as paid");
      fetchData();
    } catch (e) {
      toast.error("Failed to mark as paid");
    }
  };

  const markAsUnpaid = async (resumeId) => {
    if (!window.confirm("This will revoke the user's access to download this resume. Continue?")) return;
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.post(`${API}/admin/payments/revoke`, { resume_id: resumeId }, { headers, withCredentials: true });
      toast.success("Resume marked as unpaid");
      fetchData();
    } catch (e) {
      toast.error("Failed to mark as unpaid");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center space-x-2">
            <Crown className="h-8 w-8 text-yellow-600" />
            <span className="text-2xl font-bold text-yellow-600">Admin Panel</span>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-dashboard">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 border-2 border-blue-200 bg-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Users</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.total_users || 0}</p>
              </div>
              <Users className="h-12 w-12 text-blue-400" />
            </div>
          </Card>

          <Card className="p-6 border-2 border-green-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Resumes</p>
                <p className="text-3xl font-bold text-green-600">{stats?.total_resumes || 0}</p>
              </div>
              <FileText className="h-12 w-12 text-green-400" />
            </div>
          </Card>

          <Card className="p-6 border-2 border-purple-200 bg-purple-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Payments</p>
                <p className="text-3xl font-bold text-purple-600">{stats?.total_payments || 0}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-purple-400" />
            </div>
          </Card>

          <Card className="p-6 border-2 border-yellow-200 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-yellow-600">₦{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
              </div>
              <DollarSign className="h-12 w-12 text-yellow-400" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="resumes" data-testid="resumes-tab">Resumes</TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">Registered Users</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Name</th>
                      <th className="text-left py-3 px-4 font-semibold">Email</th>
                      <th className="text-left py-3 px-4 font-semibold">Status</th>
                      <th className="text-left py-3 px-4 font-semibold">Joined</th>
                      <th className="text-right py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 flex items-center gap-2">
                          {u.name} {u.id === user.id && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">You</span>}
                        </td>
                        <td className="py-3 px-4">{u.email}</td>
                        <td className="py-3 px-4">
                          {u.is_verified ? (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">Verified</span>
                          ) : (
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">Unverified</span>
                          )}
                        </td>
                        <td className="py-3 px-4">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 text-right space-x-2">
                          {u.id !== user.id && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleVerification(u.id, u.is_verified)}
                                className={`text-xs ${u.is_verified
                                  ? "text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                  : "text-green-600 hover:bg-green-50 hover:text-green-700"
                                  }`}
                              >
                                {u.is_verified ? "Unverify" : "Verify"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => makeAdmin(u.id, !u.is_admin)}
                                className="text-xs"
                              >
                                {u.is_admin ? "Remove Admin" : "Make Admin"}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteUser(u.id)}
                                className="text-xs"
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="resumes">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">All Resumes</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="resumes-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Title</th>
                      <th className="text-left py-3 px-4 font-semibold">Owner</th>
                      <th className="text-left py-3 px-4 font-semibold">Created</th>
                      <th className="text-right py-3 px-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumes.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{r.title}</td>
                        <td className="py-3 px-4 font-medium text-gray-700">
                          {r.user_name}<br />
                          <span className="text-xs text-gray-500 font-normal">{r.user_email}</span>
                        </td>
                        <td className="py-3 px-4">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 text-right space-x-2">
                          {r.is_paid ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => markAsUnpaid(r.id)}
                              className="text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
                            >
                              Mark Unpaid
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => markAsPaid(r.id)}
                              className="text-xs bg-green-100 text-green-700 hover:bg-green-200"
                            >
                              Mark Paid
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteResume(r.id)}
                            className="text-xs"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {resumes.length === 0 && (
                      <tr><td colSpan="4" className="text-center py-8 text-gray-500">No resumes found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">Payment History</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="payments-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Transaction ID</th>
                      <th className="text-left py-3 px-4 font-semibold">Amount</th>
                      <th className="text-left py-3 px-4 font-semibold">Status</th>
                      <th className="text-left py-3 px-4 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm">{payment.transaction_id || 'N/A'}</td>
                        <td className="py-3 px-4 font-semibold">₦{payment.amount.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${payment.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                              }`}
                          >
                            {payment.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">{new Date(payment.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {payments.length === 0 && (
                  <p className="text-center py-8 text-gray-500">No payments found</p>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
